// frontend/client/src/components/PrivacyPolicy.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Users, FileText } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-pink-600 hover:text-pink-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
        
        {/* Main Content */}
        <div className="bg-white rounded-xl p-8 md:p-12 border border-gray-200 shadow-xl">
          {/* Header */}
          <div className="flex items-center mb-6">
            <Shield className="w-10 h-10 text-pink-600 mr-4" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-500 mt-2">Last updated: June 10, 2026</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8 space-y-10">
            {/* Section 1 */}
            <section>
              <div className="flex items-start mb-4">
                <Database className="w-6 h-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
                  
                  <div className="space-y-4 text-gray-700">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">1.1 Personal Information</h3>
                      <p className="mb-2">When you create an account with Infleuince, we collect:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                        <li>Account data: Email address, username, and profile metadata.</li>
                        <li>Social media credentials: Long-lived OAuth access tokens, refresh tokens, and account profile identifiers upon explicit channel synchronization.</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">1.2 Voluntary Brand Identity Parameters</h3>
                      <p className="mb-2">To initialize personalized, cross-platform generative AI routines and brand-aligned assets, we voluntarily save data collected during setup:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                        <li>Brand Name and targeted Industry sectors.</li>
                        <li>Defined Target Audience demographics.</li>
                        <li>Distinct Brand Tones and personalized core Content Pillars including custom thematic descriptions.</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">1.3 Automated Usage and Security Telemetry</h3>
                      <p className="mb-2">We automatically capture background interaction properties to maintain site integrity:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                        <li>Device specifications, system browser variants, IP network logs, and workflow click paths.</li>
                        <li>Identity fraud assessments via automated Google reCAPTCHA tracking signals during login, account creation, and validation request phases.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Section 2 */}
            <section>
              <div className="flex items-start mb-4">
                <Lock className="w-6 h-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
                  
                  <div className="space-y-4 text-gray-700">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">2.1 Providing and Personalizing the Service</h3>
                      <p className="mb-2">Information is deployed strictly across core services to:</p>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                        <li>Orchestrate multi-engine artificial intelligence content creation setups on your behalf.</li>
                        <li>Publish and schedule multi-format media configurations straight to connected platform APIs.</li>
                        <li>Aggregate historical engagement variables (views, likes, comments, and metrics loops) into an analytical performance coach framework.</li>
                        <li>Verify token lifecycle updates and manage Stripe billing, plan migrations, and generative resource thresholds.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Section 3 */}
            <section>
              <div className="flex items-start mb-4">
                <Users className="w-6 h-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Sharing and Disclosure</h2>
                  
                  <div className="space-y-4 text-gray-700">
                    <p className="text-gray-600">
                        We maintain a strict non-commercialization model: we do not sell or trade user data. We share components exclusively with verification sub-processors:
                    </p>
                    <div>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                        <li>**Operational Infrastructure Experts:** Dedicated hosting, secure cloud processing data lakes, and transactional telemetry operations.</li>
                        <li>**Financial Processors:** Complete payment processing operations are safely managed via Stripe; credit records are never held on internal servers.</li>
                        <li>**Integrated Native Networks:** Programmatic payload passes directly to authorization platforms (Meta Graph, TikTok Open API, LinkedIn Posts API, and X/Twitter API endpoints).</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Section 4 */}
            <section>
              <div className="flex items-start mb-4">
                <FileText className="w-6 h-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Your Rights, Adjustments, and Retention Controls</h2>
                  
                  <div className="space-y-4 text-gray-700">
                    <p className="text-gray-600">
                        You preserve complete control regarding your analytical profile and metadata storage footprints:
                    </p>
                    <div>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                        <li>**Granular Retention Lifecycles:** Users can explicitly change personal data retention windows (defaulting to a maximum 12-month processing pool) inside advanced account privacy properties.</li>
                        <li>**Instant Integration De-authorizations:** Choosing to disconnect an application channel instantly deletes all long-lived tokens from active cluster caches and structural database entries.</li>
                        <li>**Account Eradication Requests:** Initiating a deletion request removes all profile metrics, brand assets, and structural properties within a hard 30-day deletion cycle.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                This Privacy Policy is effective as of June 10, 2026 and applies to all users of the Infleuince service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}